import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import 'antd/dist/antd.min.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import {ApolloProvider, ApolloClient, InMemoryCache} from '@apollo/client';
import {createUploadLink} from 'apollo-upload-client';
import {onError} from '@apollo/client/link/error';
import {from} from 'apollo-link';
import store from './redux/store';
import {setContext} from '@apollo/client/link/context';
import {Provider} from 'react-redux';
import {handlePageError} from 'redux/store/auth';
import {handleSessionExpired} from 'utils/handleSessionExpiry';
import {reloadOnMissingChunk, watchForNewVersion} from 'utils/appVersion';

// "ResizeObserver loop completed with undelivered notifications." is a benign
// browser quirk (antd's virtualized Selects fire it while their option list
// resizes during search). It breaks nothing, but the dev-server overlay shows
// it as a fatal red screen — swallow it before it reaches the overlay.
const swallowResizeObserverError = e => {
  if (e?.message?.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    [
      'webpack-dev-server-client-overlay',
      'webpack-dev-server-client-overlay-div',
    ].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    });
  }
};
window.addEventListener('error', swallowResizeObserverError);

const authLink = setContext((_, {headers}) => {
  // get the authentication token from local storage if it exists
  // return the headers to the context so httpLink can read them
  const token = localStorage.getItem('token');
  if (!token) {
    return;
  }
  return {
    headers: {
      ...headers,
      authorization: token,
      'X-Client-Origin': 'admin-dashboard',
    },
  };
});
export const handleGQLErrors = ({graphQLErrors, networkError}) => {
  const errors = [];
  if (graphQLErrors) {
    graphQLErrors.map(({message}) => {
      errors.push(message);
      return true;
    });
  }
  if (networkError) {
    errors.push(networkError.message);
    if (typeof window !== 'undefined' && !window.navigator.onLine) {
      errors.push('Browser is not connected: check your internet connection');
    }
  }
  return errors;
};
// The backend signals the error type via `extensions.code` (HTTP 200), but
// older/other responses put it on a top-level `errorTypeEnum`. Read both so we
// never miss it. Same for the status code.
const getErrorCode = err => err?.extensions?.code ?? err?.errorTypeEnum;
const getErrorStatus = err => err?.extensions?.statusCode ?? err?.statusCode;

// Some auth failures are only distinguishable by their message (e.g. an
// expired token), so match on the message text as well as the code/status.
const AUTH_MESSAGE_REGEX =
  /token expired|expired token|jwt expired|invalid token|not authenticated|unauthenticated|unauthorized/i;

const isSessionExpiredError = err =>
  getErrorCode(err) === 'AUTHENTICATION_ERROR' ||
  getErrorStatus(err) === 401 ||
  AUTH_MESSAGE_REGEX.test(err?.message ?? '');

const ErrorHandlingLink = onError(gqlErrors => {
  handleGQLErrors(gqlErrors);
  const {graphQLErrors, networkError} = gqlErrors;

  // Detect an expired / invalid session from any of the errors in the array —
  // a GraphQL auth error (e.g. { message: "Token expired",
  // extensions: { code: "AUTHENTICATION_ERROR" } }) or a raw HTTP 401 network
  // error — and force the user back to the login screen instead of letting
  // requests fail silently.
  const sessionExpired =
    graphQLErrors?.some(isSessionExpiredError) ||
    networkError?.statusCode === 401 ||
    networkError?.response?.status === 401;

  if (sessionExpired) {
    handleSessionExpired();
    return;
  }

  graphQLErrors?.forEach(err => {
    const code = getErrorCode(err);
    const statusCode = getErrorStatus(err);
    if (code === 'AUTHORIZATION_ERROR' && statusCode === 403) {
      return store.dispatch(
        handlePageError({error: {message: err?.message, statusCode}}),
      );
    } else if (code === 'INTERNAL_SERVER_ERROR') {
      return store.dispatch(
        handlePageError({error: {message: 'ERROR', statusCode: 401}}),
      );
    }
  });
});

export const client = new ApolloClient({
  link: authLink.concat(
    from([
      ErrorHandlingLink,
      createUploadLink({
        uri: `${process.env.REACT_APP_API_BASE_URL}/graphql`,
      }),
    ]),
  ), // Chain it with the HttpLink
  cache: new InMemoryCache(),
  connectToDevTools: process.env.NODE_ENV === 'development',
  queryDeduplication: true,
});
// A deploy replaces the bundle this tab is running: prompt for a refresh, and
// recover on its own if a lazy route's chunk has already gone missing.
watchForNewVersion();
reloadOnMissingChunk();

ReactDOM.render(
  <ApolloProvider client={client}>
    <Provider store={store}>
      <React.StrictMode>
        <App />
      </React.StrictMode>
    </Provider>
  </ApolloProvider>,
  document.getElementById('root'),
);
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
